import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Pencil, Trash2, Users, Building2, UserPlus } from 'lucide-react';
import type { CorporateClientRow } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Modal, Input, Textarea, Skeleton, EmptyState, useConfirm } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { getCorporateClients, createCorporateClient, updateCorporateClient, deleteCorporateClient } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';

/** Corporate clients: companies that sponsor members. See a company's people and manage its details. */
export function CorporatePage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data, loading, error, reload } = useAsync(getCorporateClients);
  const [editing, setEditing] = useState<CorporateClientRow | null | 'new'>(null);

  const clients = data ?? [];

  async function remove(c: CorporateClientRow) {
    const ok = await confirm({
      title: `Delete ${c.name}?`,
      description:
        c.memberCount > 0
          ? `${c.memberCount} member${c.memberCount === 1 ? '' : 's'} will be unlinked and kept as individuals.`
          : 'This removes the company. It cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    await deleteCorporateClient(c.id).catch(() => undefined);
    reload();
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow="People"
        title="Corporate clients"
        description="Companies that sponsor members. Track their contact, and see everyone on their account."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">Add company</span>
          </Button>
        }
      />

      {loading ? (
        <Skeleton className="h-64" />
      ) : error ? (
        <EmptyState title="Couldn’t load corporate clients" description={error} />
      ) : clients.length === 0 ? (
        <EmptyState
          title="No companies yet"
          description="Add a company that sponsors members, then link members to it from their profile."
          className="bg-surface"
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {clients.map((c) => (
            <Card key={c.id} className="flex flex-col gap-3 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-control bg-surface text-ink-soft">
                    <Building2 className="size-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">{c.name}</p>
                    <p className="truncate text-xs text-muted">
                      {c.contactName || c.contactPhone || c.contactEmail || 'No contact set'}
                    </p>
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-ink">
                  <Users className="size-3.5" aria-hidden />
                  {c.memberCount}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => navigate('/members/new', { state: { corporateId: c.id, from: '/corporate' } })}
                >
                  <UserPlus className="size-4" aria-hidden /> Add member
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/members?corporate=${c.id}&company=${encodeURIComponent(c.name)}`)}
                >
                  <Users className="size-4" aria-hidden /> View members
                </Button>
                <button
                  type="button"
                  onClick={() => setEditing(c)}
                  className="inline-flex items-center gap-1.5 rounded-control px-2.5 py-2 text-sm font-medium text-ink-soft hover:bg-surface hover:text-ink"
                >
                  <Pencil className="size-4" aria-hidden /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => remove(c)}
                  className="inline-flex items-center gap-1.5 rounded-control px-2.5 py-2 text-sm font-medium text-muted hover:bg-bad-soft hover:text-bad-ink"
                >
                  <Trash2 className="size-4" aria-hidden /> Delete
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <CompanyModal
          client={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </section>
  );
}

function CompanyModal({
  client,
  onClose,
  onSaved,
}: {
  client: CorporateClientRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(client?.name ?? '');
  const [contactName, setContactName] = useState(client?.contactName ?? '');
  const [contactPhone, setContactPhone] = useState(client?.contactPhone ?? '');
  const [contactEmail, setContactEmail] = useState(client?.contactEmail ?? '');
  const [notes, setNotes] = useState(client?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) {
      setError('A company name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    const body = {
      name: name.trim(),
      contactName: contactName.trim() || undefined,
      contactPhone: contactPhone.trim() || undefined,
      contactEmail: contactEmail.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    try {
      if (client) await updateCorporateClient(client.id, body);
      else await createCorporateClient(body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">{client ? 'Edit company' : 'Add a company'}</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <Input label="Company name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Input label="Contact person (optional)" value={contactName} onChange={(e) => setContactName(e.target.value)} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Contact phone (optional)" type="tel" inputMode="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+263 77 000 0000" />
          <Input label="Contact email (optional)" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </div>
        <Textarea label="Notes (optional)" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} loading={saving}>{client ? 'Save' : 'Add company'}</Button>
        </div>
      </div>
    </Modal>
  );
}
